import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { REPORT_REASONS, ReportReason, ContentType, submitReport } from "@/lib/moderation";

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentType: ContentType;
  contentId?: string | null;
  reportedUserId?: string | null;
  contextLabel?: string;
}

export const ReportDialog = ({ open, onOpenChange, contentType, contentId, reportedUserId, contextLabel }: ReportDialogProps) => {
  const { toast } = useToast();
  const [reason, setReason] = useState<ReportReason>("harassment");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitReport({ reportedUserId, contentType, contentId, reason, note });
      toast({ title: "Report submitted", description: "Our moderators will review it. Thanks for keeping the community safe." });
      onOpenChange(false);
      setNote("");
      setReason("harassment");
    } catch (err: any) {
      toast({ title: "Couldn't submit report", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report {contextLabel ?? "content"}</DialogTitle>
          <DialogDescription>Reports are confidential. Reported users won't see who reported them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <RadioGroup value={reason} onValueChange={(v) => setReason(v as ReportReason)}>
            {REPORT_REASONS.map((r) => (
              <div key={r.value} className="flex items-center space-x-2">
                <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                <Label htmlFor={`reason-${r.value}`} className="font-normal cursor-pointer">{r.label}</Label>
              </div>
            ))}
          </RadioGroup>
          <div className="space-y-1.5">
            <Label htmlFor="report-note" className="text-sm">Additional details <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea id="report-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Anything else moderators should know?" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>{submitting ? "Submitting…" : "Submit report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
